import { useCallback, useMemo, useRef } from 'react'
import { useSafeState } from './use-safe-state'

/**
 * A hook to manage virtual rows in a component. It works by calculating a top & bottom padding of the scroll container
 * based on its height, the number of rows and their height and the current scroll position. The padding is used to position
 * the visible rows in the visible part of the scroll-containers viewport and to ensure the correct scrollbar sizes.
 * It calculates the indices of the visible rows and the leftover space.
 *
 * @param {Object} options - The options for the hook.
 * @param {number} [options.overscan = 10] - The amount of rows to render outside of the view.
 * @param {number} options.rowHeight - The height of each row in pixels.
 * @param {number} options.scrollElementHeight - The height of the component in pixels.
 * @param {number} options.itemCount - The number of rows.
 * @param {boolean} options.virtualized - Whether to use virtualization.
 * @returns {Object} An object containing the visible indices, measurements and a scroll-callback.
 */
function useVirtualRows({ itemCount = 0, overscan = 10, rowHeight, scrollElementHeight, virtualized }) {
    const [scrollTop, setScrollTop] = useSafeState(0)

    const timer = useRef()
    const scrollTopRef = useRef(0)
    const onScroll = useCallback(
        ({ target }) => {
            scrollTopRef.current = target.scrollTop
            if (Math.abs(scrollTopRef.current - scrollTop) > (rowHeight * overscan) / 2 && !timer.current) {
                timer.current = setTimeout(() => {
                    timer.current = undefined
                    setScrollTop(scrollTopRef.current)
                }, 10)
            }
        },
        [rowHeight, scrollTop, setScrollTop, overscan]
    )

    return useMemo(() => {
        const bodyHeight = Math.max(scrollElementHeight, 0)
        let start = 0
        let end = itemCount

        if (virtualized && itemCount > 0) {
            start = Math.max(0, Math.floor(scrollTop / rowHeight) - overscan)
            end = Math.min(Math.ceil((scrollTop + bodyHeight) / rowHeight) + overscan, itemCount)
        }

        return {
            rowIndices: Array.from({ length: end - start }, (_val, index) => ({ index: start + index })),
            virtualMeasurements: {
                paddingTop: start * rowHeight,
                paddingBottom: (itemCount - end) * rowHeight,
                scrollElementHeight: bodyHeight,
                leftoverSpace: Math.max(0, bodyHeight - itemCount * rowHeight)
            },
            onScroll
        }
    }, [rowHeight, overscan, scrollElementHeight, scrollTop, itemCount, virtualized, onScroll])
}

export { useVirtualRows }
